import moment from "moment-timezone";
/** Format the pickup date to the specified format
 * @param {string} pickupDate - The pickup date to format
 * @param {string} formatDate - The desired date format
 * @returns {string} - The formatted pickup date
 */
export function formatPickupDate(pickupDate, formatDate) {
    return moment(pickupDate).utc(true).format(formatDate);
}

/** Format the pickup time to the specified format
 * @param {string} pickupTime - The pickup time to format
 * @param {string} formatTime - The desired time format
 * @returns {string} - The formatted pickup time
 */
export function formatPickupTime(pickupTime, formatTime) {
    return moment(pickupTime, ["hh:mm A"]).format(formatTime);
}

/** Format the vehicle category to a more user-friendly string
 * @param {string} category - The vehicle category to format
 * @returns {string} - The formatted vehicle category
 */
export function formatVehicleCategory(category) {
    if (category === "standard") {
        return "Standard Class";
    } else if (category === "business") {
        return "Business Class";
    } else {
        return "Luxury Class";
    }
}

/** Format the passenger type to a more user-friendly string
 * @param {string} type - The passenger type to format
 * @returns {string} - The formatted passenger type
 */
export function formatBookingType(type) {
    if (type === "airport") {
        return "Airport Transfer";
    } else if (type === "arrival") {
        return "Airport Transfer (Arrival)";
    } else if (type === "departure") {
        return "Airport Transfer (Departure)";
    } else if (type === "station") {
        return "Station Transfer";
    } else if (type === "port") {
        return "Port Transfer";
    } else if (type === "hourly") {
        return "Hourly";
    } else if (type === "journey") {
        return "Journey";
    }
}

/** Format the payment type to a more user-friendly string
 * @param {string} type - The payment type to format
 * @returns {string} - The formatted payment type
 */
export function formatPaymentType(type) {
    if (type === "ota") {
        return "OTA";
    } else if (type === "card") {
        return "Credit Card";
    } else if (type === "transfer") {
        return "Bank Transfer";
    } else if (type === "paypal") {
        return "PayPal";
    }
}

/** Format the 'from' location for a reservation request based on transfer type and selected location.
 * Airports are sent as objects on arrivals; non-airport Google selections are sent as objects
 * so the backend can geocode them by place_id. Otherwise the 'from' string is sent.
 * @param {string} from - The 'from' location string.
 * @param {string} transferType - The type of transfer (e.g., "arrival", "departure").
 * @param {Object|null} location - The selected origin data, if any.
 * @returns {string|Object} - The 'from' location for the request.
 */
export function formatFromLocationForRequest(from, transferType, location) {
    if (transferType === "arrival" && location) {
        return location;
    }

    return isSelectedGooglePlace(location, from) ? location : from;
}

/** Format the 'to' location for a reservation request based on transfer type and selected location.
 * Airports are sent as objects on departures; non-airport Google selections are sent as objects
 * so the backend can geocode them by place_id. Otherwise the 'to' string is sent.
 * @param {string} to - The 'to' location string.
 * @param {string} transferType - The type of transfer (e.g., "arrival", "departure").
 * @param {Object|null} location - The selected destination data, if any.
 * @returns {string|Object} - The 'to' location for the request.
 */
export function formatToLocationForRequest(to, transferType, location) {
    if (transferType === "departure" && location) {
        return location;
    }

    return isSelectedGooglePlace(location, to) ? location : to;
}

/** Check if a location is a non-airport Google selection that still matches the current address.
 * @param {Object|null} location - The selected location data.
 * @param {string} address - The current address string.
 * @returns {boolean}
 */
function isSelectedGooglePlace(location, address) {
    return !!location &&
        location.type !== "airport" &&
        !!location.place_id &&
        location.select === address;
}
